'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, ArrowLeft, ArrowRight } from 'lucide-react';
import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import {
  CampaignLocations,
  CampaignLocationsRef,
} from '@/components/campaign/onboarding/campaign-onboarding-locations';
import { LandingPageSelect, ONBOARDING_LINK_PREFIX } from '@/components/campaign/new/landing-page-select';
import { useWizardHost } from '@/components/campaign/new/wizard-host-context';
import { useCreateCampaignMutation, useCreateBrandCampaignMutation, businessQueryOptions, campaignQueryOptions } from '@/lib/query-options';
import { Location, CAMPAIGN_TYPE, type CampaignProximityInput } from '@/lib/types';
import { setCampaignProximityTargets } from '@/lib/apis';
import { generateCampaignSlug } from '@/lib/nanoid-utils';
import { useToast } from '@/hooks/useToast';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { useRefetchCampaigns } from '@/hooks/useRefetchCampaign';
import { useAtomValue } from 'jotai';
import { organizationDataAtom, campaignCreationPreSelectionAtom } from '@/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/prospecting')({
  component: StandardCampaign,
});

enum STANDARD_CAMPAIGN_STEP {
  SELECT_LANDING_PAGE = 'SELECT_LANDING_PAGE',
  SELECT_LOCATIONS = 'SELECT_LOCATIONS',
}

const STEP_CONFIG: Record<
  STANDARD_CAMPAIGN_STEP,
  {
    stepIndicator: number;
    title: string;
    backStep: STANDARD_CAMPAIGN_STEP | null;
  }
> = {
  [STANDARD_CAMPAIGN_STEP.SELECT_LANDING_PAGE]: {
    stepIndicator: 1,
    title: 'Choose Landing Pages',
    backStep: null,
  },
  [STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS]: {
    stepIndicator: 2,
    title: 'Where will your ads show?',
    backStep: STANDARD_CAMPAIGN_STEP.SELECT_LANDING_PAGE,
  },
};

export function StandardCampaign() {
  const { toast } = useToast();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const host = useWizardHost();
  const organizationData = useAtomValue(organizationDataAtom);
  const preSelection = useAtomValue(campaignCreationPreSelectionAtom);

  // Read selected campaign types from atom
  const selectedTypes = new Set(
    preSelection.types.length > 0
      ? preSelection.types.filter((t): t is CAMPAIGN_TYPE =>
          Object.values(CAMPAIGN_TYPE).includes(t as CAMPAIGN_TYPE)
        )
      : [CAMPAIGN_TYPE.PROSPECTING]
  );

  const createProspecting = selectedTypes.has(CAMPAIGN_TYPE.PROSPECTING);
  const createBrand = selectedTypes.has(CAMPAIGN_TYPE.BRAND);

  const { data: business, isLoading: isLoadingBusinesses } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  const getInitialStep = () => {
    return createProspecting
      ? STANDARD_CAMPAIGN_STEP.SELECT_LANDING_PAGE
      : STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS;
  };

  // State
  const [currentStep, setCurrentStep] = useState<STANDARD_CAMPAIGN_STEP>(getInitialStep);
  const [selectedLandingPage, setSelectedLandingPage] = useState<string>('');
  const [locations, setLocations] = useState<Location[]>([]);
  // #5444: radii chosen on the wizard's Radius tab, saved onto the prospecting campaign.
  const [radii, setRadii] = useState<CampaignProximityInput[]>([]);
  const [stepValid, setStepValid] = useState(false);
  const [selectedAdIds, setSelectedAdIds] = useState<Set<string>>(new Set());
  const adIdsInitializedRef = useRef(false);
  const campaignLocationsRef = useRef<CampaignLocationsRef>(null);

  // Memoized callbacks for step validation to prevent infinite re-render loops
  const setStepValidCallback = useCallback((valid: boolean) => {
    setStepValid(valid);
  }, []);

  const { refetchWithDelayedUpdates } = useRefetchCampaigns();
  const queryClient = useQueryClient();
  const createCampaignMutation = useCreateCampaignMutation();
  const createBrandCampaignMutation = useCreateBrandCampaignMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  useLoadingOverlay(isSubmitting);

  // Set defaults from business when available
  useEffect(() => {
    if (business) {
      if (!selectedLandingPage && business.business_url) {
        setSelectedLandingPage(business.business_url);
      }
      if (business.target_locations && locations.length === 0) {
        setLocations(business.target_locations);
      }
      // Select all ads by default (homepage + all products) — only on first load
      if (!adIdsInitializedRef.current) {
        adIdsInitializedRef.current = true;
        const ids = ['_homepage'];
        if (business.products_data) {
          ids.push(...business.products_data.map((p) => p.id));
        }
        setSelectedAdIds(new Set(ids));
      }
    }
  }, [business]);

  // Track page view
  useEffect(() => {
    if (business) {
      posthog?.capture('campaign_creation_started', {
        business_id: business.id,
        brand_name: business.brand_name,
        campaign_types: Array.from(selectedTypes),
      });
    }
  }, [business?.id]);

  // "Choose Ads to Create" only applies to prospecting campaigns
  const showAdSelectionStep = createProspecting;

  const handleBack = () => {
    const firstStep = showAdSelectionStep
      ? STANDARD_CAMPAIGN_STEP.SELECT_LANDING_PAGE
      : STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS;

    if (currentStep === firstStep) {
      if (host) {
        host.onExit();
      } else {
        navigate({ to: '/new/campaign' });
      }
      return;
    }

    const backStep = STEP_CONFIG[currentStep].backStep;
    if (backStep) {
      setCurrentStep(backStep);
    }
  };

  const handleNext = async () => {
    if (currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LANDING_PAGE) {
      setCurrentStep(STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS);
      return;
    }

    if (currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS) {
      const locationsApproved = campaignLocationsRef.current?.approveLocations();
      if (locationsApproved) {
        await handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    if (!business?.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      const campaignTypesCreated: string[] = [];
      const createdCampaignIds: number[] = [];
      let lastCampaignSlug: string | undefined;

      posthog?.capture('campaign_creation_submitted', {
        business_id: business.id,
        location_count: locations.length,
        landing_page: selectedLandingPage,
        campaign_types: Array.from(selectedTypes),
      });

      // Create Brand Campaign if selected
      if (createBrand) {
        const brandCampaign = await createBrandCampaignMutation.mutateAsync({
          slug: generateCampaignSlug(),
          website_url: selectedLandingPage,
          business_desc: '',
          target_locations: locations,
          budget: 0,
          goal: 'brand',
          organization_id: organizationData?.id,
          business_id: business.id,
        });

        campaignTypesCreated.push('brand');
        if (brandCampaign.id) createdCampaignIds.push(brandCampaign.id);
        lastCampaignSlug = brandCampaign.slug;
        // Seed the single-campaign cache so navigating lands on (and selects) the
        // new campaign instead of the detail loader 404ing and redirecting (#5444).
        queryClient.setQueryData(campaignQueryOptions(brandCampaign.slug).queryKey, brandCampaign);

        posthog?.capture('brand_campaign_created', {
          campaign_id: brandCampaign.id,
          campaign_slug: brandCampaign.slug,
        });
      }

      // Create Prospecting Campaign if selected
      if (createProspecting) {
        // Separate product IDs from onboarding link URLs
        const allSelectedIds = Array.from(selectedAdIds).filter((id) => id !== '_homepage');
        const productIds = allSelectedIds.filter((id) => !id.startsWith(ONBOARDING_LINK_PREFIX));
        const landingPageUrls = allSelectedIds
          .filter((id) => id.startsWith(ONBOARDING_LINK_PREFIX))
          .map((id) => id.slice(ONBOARDING_LINK_PREFIX.length));

        // Use the first selected landing page URL as campaign website_url when homepage is deselected
        const campaignWebsiteUrl = selectedAdIds.has('_homepage')
          ? selectedLandingPage
          : landingPageUrls[0] || selectedLandingPage;

        const prospectingCampaign = await createCampaignMutation.mutateAsync({
          slug: generateCampaignSlug(),
          website_url: campaignWebsiteUrl,
          business_desc: '',
          goal: 'prospecting',
          target_locations: locations,
          campaign_goal_text: undefined,
          budget: 0,
          organization_id: organizationData?.id,
          business_id: business.id,
          ad_product_ids: productIds,
          ad_landing_page_urls: landingPageUrls.length > 0 ? landingPageUrls : undefined,
          include_homepage_ad: selectedAdIds.has('_homepage'),
        });

        // #5444: persist the wizard's Radius-tab selection on the prospecting campaign.
        if (radii.length > 0 && prospectingCampaign.id) {
          try {
            await setCampaignProximityTargets(prospectingCampaign.id, radii);
          } catch {
            toast({
              description: 'Campaign created, but saving radius targets failed — set them in campaign settings.',
              variant: 'destructive',
            });
          }
        }

        campaignTypesCreated.push('prospecting');
        if (prospectingCampaign.id) createdCampaignIds.push(prospectingCampaign.id);
        lastCampaignSlug = prospectingCampaign.slug;
        // Seed the single-campaign cache (see brand branch above) — #5444.
        queryClient.setQueryData(campaignQueryOptions(prospectingCampaign.slug).queryKey, prospectingCampaign);

        posthog?.capture('prospecting_campaign_created', {
          campaign_id: prospectingCampaign.id,
          campaign_slug: prospectingCampaign.slug,
        });
      }

      await refetchWithDelayedUpdates();

      posthog?.capture('campaign_creation_completed', {
        campaign_types: campaignTypesCreated,
        total_campaigns: campaignTypesCreated.length,
      });

      if (host) {
        host.onFinished({ campaignIds: createdCampaignIds });
      } else if (lastCampaignSlug) {
        navigate({
          to: `/campaigns/google/$campaignslug`,
          params: { campaignslug: lastCampaignSlug },
        });
      } else {
        navigate({ to: '/campaigns' });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create campaigns';
      posthog?.capture('campaign_creation_failed', {
        error_message: errorMessage,
        campaign_types: Array.from(selectedTypes),
      });
      toast({
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Failed to create campaigns', error);
      setIsSubmitting(false);
    }
  };

  // Show loading state while fetching businesses
  if (isLoadingBusinesses) {
    return (
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
        <div className="flex justify-center">
          <div className="w-[60%]">
            <Card className="p-6">
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">Loading...</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no brand exists
  if (!business) {
    return (
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
        <div className="flex justify-center">
          <div className="w-[60%]">
            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Building2 className="h-12 w-12 text-gray-900 mb-4" strokeWidth={1} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your Business First</h3>
                  <p className="text-gray-700 mb-6">You need to create a business before creating campaigns.</p>
                  <Button
                    onClick={() => {
                      host?.onLeave();
                      navigate({ to: '/new/brand' });
                    }}
                    size="lg"
                    className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                  >
                    <span>CREATE BUSINESS</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Ads + Locations for prospecting; just Locations for brand-only
  const visibleSteps = showAdSelectionStep ? 2 : 1;
  const getVisibleStepIndicator = () => {
    if (showAdSelectionStep) {
      if (currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LANDING_PAGE) return 1;
      if (currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS) return 2;
    }
    return 1;
  };

  return (
    <div className="absolute inset-0 overflow-y-auto">
    <div className="max-w-5xl w-full space-y-6 mx-auto pt-8 pb-12 px-4">
      <StepsIndicator
        currentStep={getVisibleStepIndicator()}
        totalSteps={visibleSteps}
      />
      <div className="flex justify-center">
        <div className={currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS ? 'w-full' : 'w-[60%]'}>
          <h1 className="text-3xl font-bold text-center mb-8">
            {STEP_CONFIG[currentStep].title}
          </h1>
          <Card className="p-6">
            {currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LANDING_PAGE && (
              <LandingPageSelect
                businessUrl={business.business_url}
                brandName={business.brand_name ?? ''}
                products={business.products_data || []}
                setStepValid={setStepValidCallback}
                selectedAdIds={selectedAdIds}
                onSelectedAdIdsChange={setSelectedAdIds}
                onboardingLinks={business.onboarding_links}
              />
            )}

            {currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS && (
              <CampaignLocations
                ref={campaignLocationsRef}
                onNext={() => {}}
                setLocations={setLocations}
                setStepValid={setStepValidCallback}
                locations={locations}
                businessId={business.id}
                candidates={business.target_locations ?? []}
                setRadii={setRadii}
              />
            )}
          </Card>

          <div className="mx-auto flex w-full max-w-[614px] justify-between items-center pt-6">
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>BACK</span>
            </Button>

            <Button
              size="lg"
              disabled={!stepValid || isSubmitting}
              onClick={handleNext}
              className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
            >
              <span>{currentStep === STANDARD_CAMPAIGN_STEP.SELECT_LOCATIONS ? 'CREATE' : 'CONTINUE'}</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
